import { useEffect, useState } from "react"
import { Loader2, AlertTriangle, Home, CheckCircle, Gift, CreditCard } from "lucide-react"

function Checkout() {
  const [show404, setShow404] = useState(false)
  const [progress, setProgress] = useState(0)
  
  // Récupérer l'URL de redirection depuis les paramètres
  const urlParams = new URLSearchParams(window.location.search)
  const redirectUrl = urlParams.get("redirect")
  const planType = urlParams.get("plan")

  useEffect(() => {
    // Vérifier si les paramètres requis sont présents
    if (!redirectUrl || !planType) {
      setShow404(true)
      return
    }

    // Animation de la barre de progression - 100% en exactement 1.5 secondes
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval)
          return 100
        }
        return prev + 1.33 // 100 / 75 = 1.33 (75 intervalles de 20ms = 1500ms)
      })
    }, 20) // 20ms pour avoir 75 intervalles en 1.5 secondes

    // Redirection après exactement 1.5 secondes
    const timer = setTimeout(() => {
      window.location.href = redirectUrl
    }, 1500)

    return () => {
      clearTimeout(timer)
      clearInterval(progressInterval)
    }
  }, [redirectUrl, planType])

  // Afficher la page 404 si pas de paramètres
  if (show404) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="h-64 w-full" style={{
            backgroundImage: `radial-gradient(circle, #3b82f6 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="max-w-md w-full relative z-10">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 text-center relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0a1a5f]/10 to-[#002f9c]/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#0d47ff]/10 to-[#1d4ed8]/10 rounded-full translate-y-12 -translate-x-12"></div>
            
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-6 relative">
                <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center shadow-lg">
                  <AlertTriangle className="w-10 h-10 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-black text-blue-900 mb-3">
                Pagina niet gevonden
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Deze pagina is alleen toegankelijk via de selectie van een premium abonnement.
              </p>

              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gradient-to-r from-[#0a1a5f] via-[#002f9c] to-[#0d47ff] hover:from-[#002f9c] hover:to-[#0d47ff] text-white font-bold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105"
              >
                <Home className="w-5 h-5" />
                Terug naar Home
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="h-64 w-full" style={{
          backgroundImage: `radial-gradient(circle, #3b82f6 1px, transparent 1px)`,
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="max-w-md w-full relative z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 text-center relative overflow-hidden">
          {/* Background Decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0a1a5f]/10 to-[#002f9c]/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#0d47ff]/10 to-[#1d4ed8]/10 rounded-full translate-y-12 -translate-x-12"></div>
          
          <div className="relative z-10">
            {/* Logo/Icon */}
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-full border-4 border-[#0a1a5f]/20"></div>
              <div className="absolute inset-0 rounded-full border-4 border-[#0a1a5f] border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 bg-gradient-to-br from-[#0a1a5f] to-[#002f9c] rounded-full flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-lg">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-black text-blue-900 mb-3">
              Je Bestelling Voorbereiden
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              We verwerken je premium IPTV-abonnement...
            </p>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-6 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-[#0a1a5f] via-[#002f9c] to-[#0d47ff] h-3 rounded-full transition-all duration-300 ease-out shadow-lg"
                style={{width: `${progress}%`}}
              ></div>
            </div>
            
            {/* Progress Text */}
            <p className="text-sm text-gray-500 mb-6">
              {progress < 50 ? 'Gegevens controleren...' :
               progress < 80 ? 'Betaling verwerken...' :
               'Doorsturen naar de winkel...'}
            </p>
            
            {/* Features Icons */}
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-700 font-medium">Directe Activering</span>
              </div>
              <div className="flex items-center gap-2 bg-yellow-50 px-3 py-2 rounded-lg">
                <Gift className="w-4 h-4 text-yellow-600" />
                <span className="text-xs text-yellow-700 font-medium">Gratis IBO Pro Player</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-700 font-medium">Veilig Betalen</span>
              </div>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-100">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <p className="text-sm text-gray-600 font-medium">
                Automatische doorverwijzing bezig...
              </p>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Je wordt over enkele seconden doorgestuurd naar onze beveiligde winkel
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout